import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth-context";
import { Mail, Lock, User as UserIcon, LogIn, AlertCircle } from "lucide-react";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import Brand from "../components/Brand";

const LOGO =
  "https://customer-assets.emergentagent.com/job_maaya-reset/artifacts/t6ziwuh2_MAYAVERSE%20-%20LOGOS%20%286%29.png";

const AuthShell = ({ children }) => (
  <div className="app-shell" data-testid="auth-shell">
    <div className="px-6 pt-10 pb-6 flex flex-col items-center text-center">
      <img src={LOGO} alt="Maayaveda" className="w-14 h-14 object-contain mb-4" data-testid="auth-logo" />
      <Brand variant="xl" />
    </div>
    <div className="px-6 pb-12">{children}</div>
  </div>
);

const GoogleButton = ({ label }) => {
  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button
      type="button"
      onClick={handleGoogle}
      data-testid="google-signin-btn"
      className="w-full rounded-full bg-white border border-[#E2EBE6] text-[#1A2E24] py-3.5 font-medium flex items-center justify-center gap-2 hover:bg-[#F5D0D6]/20 transition-colors"
    >
      <svg className="w-4 h-4" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.99 10.99 0 0012 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18a10.99 10.99 0 000 9.86l3.66-2.84z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
      </svg>
      {label}
    </button>
  );
};

export const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErr("");
    try {
      await login({ email, password });
      const dest = location.state?.from?.pathname || "/";
      navigate(dest, { replace: true });
      toast.success("Welcome back");
    } catch (e) {
      setErr(e.response?.data?.detail || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell>
      <form onSubmit={onSubmit} className="space-y-3" data-testid="login-form">
        <h2 className="text-xl font-medium text-[#1A2E24] text-center mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
          Welcome back
        </h2>
        <div className="rounded-2xl bg-white border border-[#E2EBE6] flex items-center px-4 gap-2">
          <Mail className="w-4 h-4 text-[#5C7568]" />
          <Input
            data-testid="login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            required
            className="border-0 bg-transparent focus-visible:ring-0 px-0"
          />
        </div>
        <div className="rounded-2xl bg-white border border-[#E2EBE6] flex items-center px-4 gap-2">
          <Lock className="w-4 h-4 text-[#5C7568]" />
          <Input
            data-testid="login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            required
            className="border-0 bg-transparent focus-visible:ring-0 px-0"
          />
        </div>
        {err && (
          <div className="rounded-2xl bg-[#F5D0D6]/40 border border-[#F5D0D6] px-4 py-3 flex items-start gap-2 text-sm text-[#1A2E24]" data-testid="login-error">
            <AlertCircle className="w-4 h-4 mt-0.5 text-[#D6336C]" />
            <span>{err}</span>
          </div>
        )}
        <button
          type="submit"
          disabled={submitting}
          data-testid="login-submit-btn"
          className="w-full rounded-full bg-[#234B38] hover:bg-[#1A382A] disabled:opacity-60 text-white py-3.5 font-medium flex items-center justify-center gap-2 transition-colors"
        >
          <LogIn className="w-4 h-4" />
          {submitting ? "Signing in…" : "Sign in"}
        </button>
        <div className="flex items-center gap-3 my-4">
          <div className="flex-1 h-px bg-[#E2EBE6]" />
          <span className="text-[10px] uppercase tracking-wider text-[#5C7568]">or</span>
          <div className="flex-1 h-px bg-[#E2EBE6]" />
        </div>
        <GoogleButton label="Continue with Google" />
        <div className="text-center text-sm text-[#5C7568] mt-6">
          <Link to="/forgot-password" className="text-[#234B38] font-medium" data-testid="forgot-password-link">Forgot password?</Link>
        </div>
        <div className="text-center text-sm text-[#5C7568] mt-2">
          New here?{" "}
          <Link to="/signup" className="text-[#234B38] font-medium" data-testid="signup-link">Create an account</Link>
        </div>
      </form>
    </AuthShell>
  );
};

export const Signup = () => {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErr("");
    try {
      await signup(form);
      navigate("/", { replace: true });
      toast.success("Account created · 7-day Pro trial started");
    } catch (e) {
      setErr(e.response?.data?.detail || "Signup failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell>
      <form onSubmit={onSubmit} className="space-y-3" data-testid="signup-form">
        <h2 className="text-xl font-medium text-[#1A2E24] text-center mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>
          Create your account
        </h2>
        <p className="text-xs text-[#5C7568] text-center mb-4">
          Includes a <span className="text-[#D6336C] font-semibold">7-day Pro trial</span>. No card required.
        </p>
        <div className="rounded-2xl bg-white border border-[#E2EBE6] flex items-center px-4 gap-2">
          <UserIcon className="w-4 h-4 text-[#5C7568]" />
          <Input data-testid="signup-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" required className="border-0 bg-transparent focus-visible:ring-0 px-0" />
        </div>
        <div className="rounded-2xl bg-white border border-[#E2EBE6] flex items-center px-4 gap-2">
          <Mail className="w-4 h-4 text-[#5C7568]" />
          <Input data-testid="signup-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" required className="border-0 bg-transparent focus-visible:ring-0 px-0" />
        </div>
        <div className="rounded-2xl bg-white border border-[#E2EBE6] flex items-center px-4 gap-2">
          <Lock className="w-4 h-4 text-[#5C7568]" />
          <Input data-testid="signup-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Password (min 6 chars)" required minLength={6} className="border-0 bg-transparent focus-visible:ring-0 px-0" />
        </div>
        {err && (
          <div className="rounded-2xl bg-[#F5D0D6]/40 border border-[#F5D0D6] px-4 py-3 flex items-start gap-2 text-sm text-[#1A2E24]" data-testid="signup-error">
            <AlertCircle className="w-4 h-4 mt-0.5 text-[#D6336C]" />
            <span>{err}</span>
          </div>
        )}
        <button type="submit" disabled={submitting} data-testid="signup-submit-btn"
          className="w-full rounded-full bg-[#234B38] hover:bg-[#1A382A] disabled:opacity-60 text-white py-3.5 font-medium transition-colors">
          {submitting ? "Creating…" : "Start my 7-day trial"}
        </button>
        <div className="flex items-center gap-3 my-4">
          <div className="flex-1 h-px bg-[#E2EBE6]" />
          <span className="text-[10px] uppercase tracking-wider text-[#5C7568]">or</span>
          <div className="flex-1 h-px bg-[#E2EBE6]" />
        </div>
        <GoogleButton label="Continue with Google" />
        <div className="text-center text-sm text-[#5C7568] mt-6">
          Already have an account?{" "}
          <Link to="/login" className="text-[#234B38] font-medium" data-testid="login-link">Sign in</Link>
        </div>
        <p className="text-[10px] text-[#5C7568] text-center mt-5 leading-relaxed">
          By continuing you agree to our{" "}
          <Link to="/terms" className="underline">Terms</Link> and{" "}
          <Link to="/privacy" className="underline">Privacy Policy</Link>.
        </p>
      </form>
    </AuthShell>
  );
};

export const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const { api } = await import("../lib/api");
      const r = await api.forgotPassword(email);
      setSubmitted(true);
      if (r.data.reset_token) setResetToken(r.data.reset_token); // demo mode
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to send reset link");
    }
  };

  return (
    <AuthShell>
      <form onSubmit={onSubmit} className="space-y-3" data-testid="forgot-form">
        <h2 className="text-xl font-medium text-[#1A2E24] text-center mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
          Reset your password
        </h2>
        <div className="rounded-2xl bg-white border border-[#E2EBE6] flex items-center px-4 gap-2">
          <Mail className="w-4 h-4 text-[#5C7568]" />
          <Input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" required className="border-0 bg-transparent focus-visible:ring-0 px-0" />
        </div>
        {err && <p className="text-sm text-[#D6336C]">{err}</p>}
        <button type="submit" data-testid="forgot-submit-btn"
          className="w-full rounded-full bg-[#234B38] hover:bg-[#1A382A] text-white py-3.5 font-medium">
          Send reset link
        </button>
        {submitted && (
          <div className="rounded-2xl bg-[#F5D0D6]/40 border border-[#F5D0D6] px-4 py-3 text-sm text-[#1A2E24]" data-testid="forgot-success">
            <p>If an account exists, a reset link has been sent.</p>
            {resetToken && (
              <p className="mt-2 text-xs text-[#5C7568] break-all">
                <span className="font-semibold">Demo:</span> use this token to reset →{" "}
                <Link to={`/reset-password?token=${resetToken}`} className="underline text-[#234B38]" data-testid="reset-link">
                  Reset now
                </Link>
              </p>
            )}
          </div>
        )}
        <div className="text-center text-sm text-[#5C7568] mt-6">
          <Link to="/login" className="text-[#234B38] font-medium">Back to sign in</Link>
        </div>
      </form>
    </AuthShell>
  );
};

export const ResetPassword = () => {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const [token] = useState(params.get("token") || "");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const { api } = await import("../lib/api");
      await api.resetPassword({ token, new_password: password });
      setDone(true);
      toast.success("Password reset · sign in to continue");
      setTimeout(() => navigate("/login"), 1500);
    } catch (e) {
      setErr(e.response?.data?.detail || "Reset failed");
    }
  };

  return (
    <AuthShell>
      <form onSubmit={onSubmit} className="space-y-3" data-testid="reset-form">
        <h2 className="text-xl font-medium text-[#1A2E24] text-center mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
          Choose a new password
        </h2>
        <div className="rounded-2xl bg-white border border-[#E2EBE6] flex items-center px-4 gap-2">
          <Lock className="w-4 h-4 text-[#5C7568]" />
          <Input data-testid="reset-password-input" type="password" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password (min 6 chars)" required className="border-0 bg-transparent focus-visible:ring-0 px-0" />
        </div>
        {err && <p className="text-sm text-[#D6336C]">{err}</p>}
        {done && <p className="text-sm text-[#234B38]">Password updated. Redirecting…</p>}
        <button type="submit" data-testid="reset-submit-btn" className="w-full rounded-full bg-[#234B38] hover:bg-[#1A382A] text-white py-3.5 font-medium">
          Update password
        </button>
      </form>
    </AuthShell>
  );
};
